import React, { useState } from "react";
import { useTabs } from "../contexts/TabsContext";
import {
    LayoutDashboard, Calendar, Wallet, CheckSquare, Megaphone,
    Users, Plane, MessageCircle, Settings, MessageSquare, Phone,
    Sparkles, StickyNote, Clock, Folder, ShieldCheck, Briefcase,
    X, Pin, PinOff, RotateCcw, MoreHorizontal, Mail,
} from "lucide-react";

const ICONS = {
    dashboard: LayoutDashboard,
    calendar: Calendar,
    banking: Wallet,
    todos: CheckSquare,
    announcements: Megaphone,
    employees: Users,
    vacations: Plane,
    chat: MessageCircle,
    settings: Settings,
    superchat: MessageSquare,
    whatsapp: Phone,
    claude: Sparkles,
    mail: Mail,
    notes: StickyNote,
    timetrack: Clock,
    drive: Folder,
    admin: ShieldCheck,
    space: Briefcase,
};

/**
 * TabsBar — Browser-style horizontal scrollable strip above the page.
 * Pinned tabs render first (with a pin badge & no close X).
 * Each tab has Click → focus, X → close, right-click menu for pin/rename.
 */
export default function TabsBar() {
    const { tabs, activeRoute, focusTab, closeTab, togglePin, resetTabs } = useTabs();
    const [menuFor, setMenuFor] = useState(null); // {id, x, y}

    if (tabs.length === 0) return null;

    return (
        <div
            className="bg-white border-b border-neutral-200 flex items-center gap-0 overflow-x-auto scrollbar-thin sticky top-0 z-30"
            onClick={() => setMenuFor(null)}
            data-testid="tabs-bar"
        >
            {tabs.map((tab) => {
                const Icon = ICONS[tab.icon] || LayoutDashboard;
                const isActive = tab.route === activeRoute;
                return (
                    <div
                        key={tab.id}
                        onClick={() => focusTab(tab.route)}
                        onAuxClick={(e) => { if (e.button === 1) { e.preventDefault(); closeTab(tab.id); } }}
                        onContextMenu={(e) => {
                            e.preventDefault();
                            setMenuFor({ id: tab.id, x: e.clientX, y: e.clientY });
                        }}
                        className={`group flex-shrink-0 flex items-center gap-1.5 px-3 h-9 border-r border-neutral-200 cursor-pointer transition-colors ${
                            isActive
                                ? "bg-white border-b-2 border-b-brand-500 text-brand-900"
                                : "bg-neutral-50 text-neutral-600 hover:bg-neutral-100"
                        }`}
                        data-testid={`tab-${tab.id}`}
                        data-active={isActive ? "true" : "false"}
                        title={tab.title}
                    >
                        {tab.pinned && <Pin className="w-3 h-3 text-brand-500 fill-brand-500 flex-shrink-0" />}
                        <Icon className="w-3.5 h-3.5 flex-shrink-0" />
                        {!tab.pinned && (
                            <span className="text-xs font-medium max-w-[140px] truncate">{tab.title}</span>
                        )}
                        {!tab.pinned && (
                            <button
                                onClick={(e) => { e.stopPropagation(); closeTab(tab.id); }}
                                className="ml-1 rounded p-0.5 opacity-0 group-hover:opacity-100 hover:bg-neutral-200 transition"
                                data-testid={`tab-close-${tab.id}`}
                                aria-label="Close tab"
                            >
                                <X className="w-3 h-3" />
                            </button>
                        )}
                    </div>
                );
            })}

            {tabs.length > 0 && (
                <button
                    onClick={() => {
                        if (window.confirm("Alle Tabs schließen? (Angeheftete bleiben offen)")) resetTabs();
                    }}
                    className="flex-shrink-0 ml-auto px-2 h-9 text-neutral-400 hover:text-red-500 hover:bg-red-50 transition flex items-center gap-1 text-xs"
                    title="Tabs zurücksetzen"
                    data-testid="tabs-reset"
                >
                    <RotateCcw className="w-3.5 h-3.5" />
                    <span className="hidden sm:inline">Reset</span>
                </button>
            )}

            {/* Context menu */}
            {menuFor && (
                <div
                    className="fixed z-50 bg-white border border-neutral-200 rounded-md shadow-lg py-1 text-sm min-w-[160px]"
                    style={{ left: Math.min(menuFor.x, window.innerWidth - 180), top: Math.min(menuFor.y, window.innerHeight - 120) }}
                    onClick={(e) => e.stopPropagation()}
                    data-testid="tab-context-menu"
                >
                    <button
                        onClick={() => { togglePin(menuFor.id); setMenuFor(null); }}
                        className="w-full text-left px-3 py-1.5 hover:bg-neutral-50 flex items-center gap-2"
                        data-testid="tab-ctx-pin"
                    >
                        {tabs.find((t) => t.id === menuFor.id)?.pinned ? (
                            <><PinOff className="w-3.5 h-3.5" /> Tab loslösen</>
                        ) : (
                            <><Pin className="w-3.5 h-3.5" /> Tab anheften</>
                        )}
                    </button>
                    <button
                        onClick={() => { closeTab(menuFor.id); setMenuFor(null); }}
                        className="w-full text-left px-3 py-1.5 hover:bg-red-50 hover:text-red-600 flex items-center gap-2"
                        data-testid="tab-ctx-close"
                    >
                        <X className="w-3.5 h-3.5" /> Tab schließen
                    </button>
                </div>
            )}
        </div>
    );
}
